import React, { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, CheckCircleIcon, FileTextIcon, UserIcon, BookOpenIcon, CalendarIcon, GraduationCapIcon, MapPinIcon, BriefcaseIcon, SparklesIcon, SaveIcon, CheckIcon } from 'lucide-react';
// Sample scholarship data (in a real app, this would come from an API)
const scholarshipsData = [{
  id: 'sch001',
  title: 'Elite Track: Computer Science at MIT',
  track: 'Elite',
  university: 'Massachusetts Institute of Technology',
  program: 'Computer Science',
  location: 'United States',
  deadline: '2023-12-15',
  coverageTypes: ['Tuition', 'Living Allowance', 'Travel', 'Books & Materials'],
  description: 'Full scholarship for outstanding Emirati students to pursue Computer Science at MIT, with a focus on AI and machine learning.',
  eligibility: ['UAE Nationals', 'GPA 3.8+', 'IELTS 7.5+'],
  logo: 'MIT'
}, {
  id: 'sch003',
  title: 'ADNOC Future Energy Leaders',
  track: 'Partnership',
  university: 'Stanford University',
  program: 'Petroleum Engineering',
  location: 'United States',
  deadline: '2023-11-15',
  coverageTypes: ['Tuition', 'Living Allowance', 'Travel', 'Books & Materials'],
  description: 'ADNOC-sponsored scholarship for future energy leaders with guaranteed employment upon graduation.',
  eligibility: ['UAE Nationals', 'GPA 3.5+', 'IELTS 6.5+'],
  employer: 'ADNOC',
  logo: 'ADNOC'
}, {
  id: 'sch004',
  title: 'Mubadala Tech Innovators',
  track: 'Partnership',
  university: 'Multiple Options',
  program: 'Computer Science / Data Science',
  location: 'Multiple Locations',
  deadline: '2023-12-01',
  coverageTypes: ['Tuition', 'Living Allowance'],
  description: 'Mubadala-sponsored scholarship for tech innovators with flexible university options from the approved list.',
  eligibility: ['UAE Nationals', 'GPA 3.5+', 'IELTS 6.5+'],
  employer: 'Mubadala',
  logo: 'Mubadala'
}, {
  id: 'sch006',
  title: 'Emirates Future Leaders',
  track: 'Partnership',
  university: 'Multiple Options',
  program: 'Aviation Management',
  location: 'Multiple Locations',
  deadline: '2023-11-20',
  coverageTypes: ['Tuition', 'Living Allowance', 'Travel'],
  description: 'Emirates-sponsored scholarship for future aviation leaders with guaranteed employment upon graduation.',
  eligibility: ['UAE Nationals', 'GPA 3.3+', 'IELTS 6.5+'],
  employer: 'Emirates',
  logo: 'Emirates'
}];
const ScholarshipApplication: React.FC = () => {
  const navigate = useNavigate();
  const {
    scholarshipId
  } = useParams();
  const [scholarship, setScholarship] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    personalInfo: {
      fullName: '',
      email: '',
      phone: '',
      dateOfBirth: '',
      nationality: 'UAE',
      passportNumber: '',
      currentAddress: '',
      permanentAddress: ''
    },
    academicInfo: {
      highestDegree: '',
      institution: '',
      major: '',
      gpa: '',
      graduationDate: '',
      englishProficiency: '',
      englishScore: '',
      otherTests: ''
    },
    documents: {
      resume: null,
      transcripts: null,
      recommendationLetters: null,
      statementOfPurpose: null,
      englishCertificate: null,
      passport: null
    },
    questions: {
      careerGoals: '',
      motivation: '',
      contribution: '',
      challenges: '',
      additionalInfo: ''
    }
  });
  useEffect(() => {
    // In a real app, this would be an API call
    const foundScholarship = scholarshipsData.find(s => s.id === scholarshipId);
    setScholarship(foundScholarship);
  }, [scholarshipId]);
  const steps = [{
    id: 1,
    name: 'Scholarship Details',
    icon: <FileTextIcon className="w-5 h-5" data-id="element-4962" />
  }, {
    id: 2,
    name: 'Personal Information',
    icon: <UserIcon className="w-5 h-5" data-id="element-4963" />
  }, {
    id: 3,
    name: 'Academic Information',
    icon: <GraduationCapIcon className="w-5 h-5" data-id="element-4964" />
  }, {
    id: 4,
    name: 'Document Upload',
    icon: <FileTextIcon className="w-5 h-5" data-id="element-4965" />
  }, {
    id: 5,
    name: 'Questionnaire',
    icon: <BookOpenIcon className="w-5 h-5" data-id="element-4966" />
  }, {
    id: 6,
    name: 'Review & Submit',
    icon: <CheckCircleIcon className="w-5 h-5" data-id="element-4967" />
  }];
  const handleNext = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };
  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };
  const handleInputChange = (section, field, value) => {
    setFormData({
      ...formData,
      [section]: {
        ...formData[section],
        [field]: value
      }
    });
  };
  const handleFileChange = (field, file) => {
    setFormData({
      ...formData,
      documents: {
        ...formData.documents,
        [field]: file
      }
    });
  };
  const handleSubmit = () => {
    // In a real app, you would submit the form data to your backend here
    console.log('Submitting application:', formData);
    // Navigate to confirmation page
    navigate('/student/application-confirmation');
  };
  const handleSaveDraft = () => {
    // In a real app, you would save the draft to your backend here
    console.log('Saving draft:', formData);
    alert('Application draft saved successfully!');
  };
  // Format date for display
  const formatDate = dateString => {
    const options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  if (!scholarship) {
    return <Layout title="Scholarship Application" persona="Student" data-id="element-4968">
        <div className="max-w-5xl mx-auto bg-white rounded-lg border border-gray-200 shadow-sm p-8 text-center" data-id="element-4969">
          <p data-id="element-4970">Loading scholarship details...</p>
        </div>
      </Layout>;
  }
  const isElite = scholarship.track === 'Elite';
  // Render the current step
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <div className="space-y-6" data-id="element-4971">
            <h2 className="text-xl font-semibold text-gray-900 mb-4" data-id="element-4972">
              Scholarship Information
            </h2>
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-6" data-id="element-4973">
              <div className="flex items-center mb-4" data-id="element-4974">
                <div className={`h-12 w-12 rounded-xl flex items-center justify-center text-white text-lg font-bold mr-4 ${isElite ? 'bg-primary-600' : 'bg-secondary-600'}`} data-id="element-4975">
                  {scholarship.logo}
                </div>
                <div data-id="element-4976">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-4977">
                    {scholarship.title}
                  </h3>
                  <div className="flex items-center mt-1" data-id="element-4978">
                    {isElite ? <div className="flex items-center bg-primary-100 text-primary-800 px-2 py-0.5 rounded-full text-xs font-medium" data-id="element-4979">
                        <SparklesIcon className="w-3 h-3 mr-1" data-id="element-4980" />
                        Elite Track
                      </div> : <div className="flex items-center bg-secondary-100 text-secondary-800 px-2 py-0.5 rounded-full text-xs font-medium" data-id="element-4981">
                        <BriefcaseIcon className="w-3 h-3 mr-1" data-id="element-4982" />
                        Partnership
                      </div>}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6" data-id="element-4983">
                <div data-id="element-4984">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-4985">University</p>
                  <div className="flex items-center" data-id="element-4986">
                    <GraduationCapIcon className="w-4 h-4 text-gray-500 mr-2" data-id="element-4987" />
                    <p className="text-gray-900" data-id="element-4988">{scholarship.university}</p>
                  </div>
                </div>
                <div data-id="element-4989">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-4990">Program</p>
                  <div className="flex items-center" data-id="element-4991">
                    <BookOpenIcon className="w-4 h-4 text-gray-500 mr-2" data-id="element-4992" />
                    <p className="text-gray-900" data-id="element-4993">{scholarship.program}</p>
                  </div>
                </div>
                <div data-id="element-4994">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-4995">Location</p>
                  <div className="flex items-center" data-id="element-4996">
                    <MapPinIcon className="w-4 h-4 text-gray-500 mr-2" data-id="element-4997" />
                    <p className="text-gray-900" data-id="element-4998">{scholarship.location}</p>
                  </div>
                </div>
                <div data-id="element-4999">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-5000">
                    Application Deadline
                  </p>
                  <div className="flex items-center" data-id="element-5001">
                    <CalendarIcon className="w-4 h-4 text-gray-500 mr-2" data-id="element-5002" />
                    <p className="text-gray-900" data-id="element-5003">
                      {formatDate(scholarship.deadline)}
                    </p>
                  </div>
                </div>
                {scholarship.employer && <div data-id="element-5004">
                    <p className="text-sm text-gray-500 mb-1" data-id="element-5005">Employer</p>
                    <div className="flex items-center" data-id="element-5006">
                      <BriefcaseIcon className="w-4 h-4 text-gray-500 mr-2" data-id="element-5007" />
                      <p className="text-gray-900" data-id="element-5008">{scholarship.employer}</p>
                    </div>
                  </div>}
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-6" data-id="element-5009">
              <h3 className="font-medium text-gray-900 mb-3" data-id="element-5010">
                Scholarship Description
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-5011">{scholarship.description}</p>
              <h4 className="font-medium text-gray-900 mb-2" data-id="element-5012">
                Eligibility Requirements
              </h4>
              <ul className="list-disc pl-5 mb-4 text-gray-700 space-y-1" data-id="element-5013">
                {scholarship.eligibility.map((item, index) => <li key={index} data-id="element-5014">{item}</li>)}
              </ul>
              <h4 className="font-medium text-gray-900 mb-2" data-id="element-5015">Coverage</h4>
              <div className="flex flex-wrap gap-2 mb-4" data-id="element-5016">
                {scholarship.coverageTypes.map((type, index) => <span key={index} className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${isElite ? 'bg-primary-100 text-primary-800' : 'bg-green-100 text-green-800'}`} data-id="element-5017">
                    {type}
                  </span>)}
              </div>
            </div>
            <div className="bg-yellow-50 rounded-lg border border-yellow-200 p-4" data-id="element-5018">
              <h3 className="font-medium text-yellow-800 mb-2 flex items-center" data-id="element-5019">
                <CalendarIcon className="w-5 h-5 mr-2" data-id="element-5020" />
                Important Dates
              </h3>
              <p className="text-yellow-700 text-sm" data-id="element-5021">
                Application Deadline: {formatDate(scholarship.deadline)}. Late
                applications will not be accepted.
              </p>
            </div>
            <p className="text-gray-700" data-id="element-5022">
              By proceeding with this application, you confirm that you meet all
              eligibility requirements for this scholarship program.
            </p>
          </div>;
      case 2:
        return <div className="space-y-6" data-id="element-5023">
            <h2 className="text-xl font-semibold text-gray-900 mb-4" data-id="element-5024">
              Personal Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5025">
              <div data-id="element-5026">
                <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5027">
                  Full Name (as on passport)
                </label>
                <input type="text" id="fullName" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500" value={formData.personalInfo.fullName} onChange={e => handleInputChange('personalInfo', 'fullName', e.target.value)} required data-id="element-5028" />
              </div>
              <div data-id="element-5029">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5030">
                  Email Address
                </label>
                <input type="email" id="email" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500" value={formData.personalInfo.email} onChange={e => handleInputChange('personalInfo', 'email', e.target.value)} required data-id="element-5031" />
              </div>
              <div data-id="element-5032">
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5033">
                  Phone Number
                </label>
                <input type="tel" id="phone" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500" value={formData.personalInfo.phone} onChange={e => handleInputChange('personalInfo', 'phone', e.target.value)} required data-id="element-5034" />
              </div>
              <div data-id="element-5035">
                <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5036">
                  Date of Birth
                </label>
                <input type="date" id="dateOfBirth" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500" value={formData.personalInfo.dateOfBirth} onChange={e => handleInputChange('personalInfo', 'dateOfBirth', e.target.value)} required data-id="element-5037" />
              </div>
              <div data-id="element-5038">
                <label htmlFor="nationality" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5039">
                  Nationality
                </label>
                <select id="nationality" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500" value={formData.personalInfo.nationality} onChange={e => handleInputChange('personalInfo', 'nationality', e.target.value)} required data-id="element-5040">
                  <option value="UAE" data-id="element-5041">United Arab Emirates</option>
                </select>
              </div>
              <div data-id="element-5042">
                <label htmlFor="passportNumber" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5043">
                  Passport/ID Number
                </label>
                <input type="text" id="passportNumber" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500" value={formData.personalInfo.passportNumber} onChange={e => handleInputChange('personalInfo', 'passportNumber', e.target.value)} required data-id="element-5044" />
              </div>
              <div className="md:col-span-2" data-id="element-5045">
                <label htmlFor="currentAddress" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5046">
                  Current Address
                </label>
                <textarea id="currentAddress" rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500" value={formData.personalInfo.currentAddress} onChange={e => handleInputChange('personalInfo', 'currentAddress', e.target.value)} required data-id="element-5047" />
              </div>
              <div className="md:col-span-2" data-id="element-5048">
                <label htmlFor="permanentAddress" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5049">
                  Permanent Address (if different)
                </label>
                <textarea id="permanentAddress" rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500" value={formData.personalInfo.permanentAddress} onChange={e => handleInputChange('personalInfo', 'permanentAddress', e.target.value)} data-id="element-5050" />
              </div>
            </div>
          </div>;
      case 3:
        return <div className="space-y-6" data-id="element-5051">
            <h2 className="text-xl font-semibold text-gray-900 mb-4" data-id="element-5052">
              Academic Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5053">
              <div data-id="element-5054">
                <label htmlFor="highestDegree" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5055">
                  Highest Degree Obtained
                </label>
                <select id="highestDegree" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500" value={formData.academicInfo.highestDegree} onChange={e => handleInputChange('academicInfo', 'highestDegree', e.target.value)} required data-id="element-5056">
                  <option value="" data-id="element-5057">Select Degree</option>
                  <option value="High School" data-id="element-5058">High School</option>
                  <option value="Bachelor's" data-id="element-5059">Bachelor's Degree</option>
                  <option value="Master's" data-id="element-5060">Master's Degree</option>
                  <option value="PhD" data-id="element-5061">PhD</option>
                </select>
              </div>
              <div data-id="element-5062">
                <label htmlFor="institution" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5063">
                  Institution Name
                </label>
                <input type="text" id="institution" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500" value={formData.academicInfo.institution} onChange={e => handleInputChange('academicInfo', 'institution', e.target.value)} required data-id="element-5064" />
              </div>
              <div data-id="element-5065">
                <label htmlFor="major" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5066">
                  Major/Field of Study
                </label>
                <input type="text" id="major" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500" value={formData.academicInfo.major} onChange={e => handleInputChange('academicInfo', 'major', e.target.value)} required data-id="element-5067" />
              </div>
              <div data-id="element-5068">
                <label htmlFor="gpa" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5069">
                  GPA (out of 4.0)
                </label>
                <input type="number" step="0.01" min="0" max="4.0" id="gpa" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500" value={formData.academicInfo.gpa} onChange={e => handleInputChange('academicInfo', 'gpa', e.target.value)} required data-id="element-5070" />
              </div>
              <div data-id="element-5071">
                <label htmlFor="graduationDate" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5072">
                  Graduation Date (or Expected)
                </label>
                <input type="date" id="graduationDate" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500" value={formData.academicInfo.graduationDate} onChange={e => handleInputChange('academicInfo', 'graduationDate', e.target.value)} required data-id="element-5073" />
              </div>
              <div data-id="element-5074">
                <label htmlFor="englishProficiency" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5075">
                  English Proficiency Test
                </label>
                <select id="englishProficiency" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500" value={formData.academicInfo.englishProficiency} onChange={e => handleInputChange('academicInfo', 'englishProficiency', e.target.value)} required data-id="element-5076">
                  <option value="" data-id="element-5077">Select Test</option>
                  <option value="IELTS" data-id="element-5078">IELTS</option>
                  <option value="TOEFL" data-id="element-5079">TOEFL</option>
                  <option value="Duolingo" data-id="element-5080">Duolingo English Test</option>
                  <option value="Other" data-id="element-5081">Other</option>
                </select>
              </div>
              <div data-id="element-5082">
                <label htmlFor="englishScore" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5083">
                  English Test Score
                </label>
                <input type="text" id="englishScore" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500" value={formData.academicInfo.englishScore} onChange={e => handleInputChange('academicInfo', 'englishScore', e.target.value)} required data-id="element-5084" />
              </div>
              <div className="md:col-span-2" data-id="element-5085">
                <label htmlFor="otherTests" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5086">
                  Other Test Scores (if applicable)
                </label>
                <textarea id="otherTests" rows={2} placeholder="e.g., GRE: 320, GMAT: 720, etc." className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500" value={formData.academicInfo.otherTests} onChange={e => handleInputChange('academicInfo', 'otherTests', e.target.value)} data-id="element-5087" />
              </div>
            </div>
          </div>;
      case 4:
        return <div className="space-y-6" data-id="element-5088">
            <h2 className="text-xl font-semibold text-gray-900 mb-4" data-id="element-5089">
              Document Upload
            </h2>
            <p className="text-gray-600 mb-6" data-id="element-5090">
              Please upload all required documents in PDF format. Each file
              should not exceed 5MB.
            </p>
            <div className="space-y-4" data-id="element-5091">
              <div className="border border-gray-200 rounded-lg p-4" data-id="element-5092">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-5093">
                  Resume/CV *
                </label>
                <div className="flex items-center" data-id="element-5094">
                  <input type="file" accept=".pdf" className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100" onChange={e => handleFileChange('resume', e.target.files[0])} data-id="element-5095" />
                </div>
                <p className="mt-1 text-xs text-gray-500" data-id="element-5096">
                  Upload your most recent resume/CV
                </p>
              </div>
              <div className="border border-gray-200 rounded-lg p-4" data-id="element-5097">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-5098">
                  Academic Transcripts *
                </label>
                <div className="flex items-center" data-id="element-5099">
                  <input type="file" accept=".pdf" className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100" onChange={e => handleFileChange('transcripts', e.target.files[0])} data-id="element-5100" />
                </div>
                <p className="mt-1 text-xs text-gray-500" data-id="element-5101">
                  Upload official transcripts from your institution
                </p>
              </div>
              <div className="border border-gray-200 rounded-lg p-4" data-id="element-5102">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-5103">
                  Recommendation Letters *
                </label>
                <div className="flex items-center" data-id="element-5104">
                  <input type="file" accept=".pdf" className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100" onChange={e => handleFileChange('recommendationLetters', e.target.files[0])} data-id="element-5105" />
                </div>
                <p className="mt-1 text-xs text-gray-500" data-id="element-5106">
                  Combine all recommendation letters into a single PDF
                </p>
              </div>
              <div className="border border-gray-200 rounded-lg p-4" data-id="element-5107">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-5108">
                  Statement of Purpose *
                </label>
                <div className="flex items-center" data-id="element-5109">
                  <input type="file" accept=".pdf" className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100" onChange={e => handleFileChange('statementOfPurpose', e.target.files[0])} data-id="element-5110" />
                </div>
                <p className="mt-1 text-xs text-gray-500" data-id="element-5111">
                  Upload your statement of purpose (max 2 pages)
                </p>
              </div>
              <div className="border border-gray-200 rounded-lg p-4" data-id="element-5112">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-5113">
                  English Proficiency Certificate *
                </label>
                <div className="flex items-center" data-id="element-5114">
                  <input type="file" accept=".pdf" className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100" onChange={e => handleFileChange('englishCertificate', e.target.files[0])} data-id="element-5115" />
                </div>
                <p className="mt-1 text-xs text-gray-500" data-id="element-5116">
                  Upload your IELTS/TOEFL/Duolingo certificate
                </p>
              </div>
              <div className="border border-gray-200 rounded-lg p-4" data-id="element-5117">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-5118">
                  Passport Copy *
                </label>
                <div className="flex items-center" data-id="element-5119">
                  <input type="file" accept=".pdf" className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100" onChange={e => handleFileChange('passport', e.target.files[0])} data-id="element-5120" />
                </div>
                <p className="mt-1 text-xs text-gray-500" data-id="element-5121">
                  Upload a clear copy of your passport
                </p>
              </div>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4" data-id="element-5122">
              <p className="text-yellow-800 text-sm" data-id="element-5123">
                <strong data-id="element-5124">Note:</strong> All documents must be in English or
                accompanied by certified translations. Original documents may be
                requested at a later stage.
              </p>
            </div>
          </div>;
      case 5:
        return <div className="space-y-6" data-id="element-5125">
            <h2 className="text-xl font-semibold text-gray-900 mb-4" data-id="element-5126">
              Questionnaire
            </h2>
            <p className="text-gray-600 mb-6" data-id="element-5127">
              Please answer the following questions thoughtfully. Your responses
              help us understand your motivations and goals.
            </p>
            <div className="space-y-4" data-id="element-5128">
              <div data-id="element-5129">
                <label htmlFor="careerGoals" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-5130">
                  What are your career goals and how does this scholarship align
                  with them? *
                </label>
                <textarea id="careerGoals" rows={4} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500" value={formData.questions.careerGoals} onChange={e => handleInputChange('questions', 'careerGoals', e.target.value)} required data-id="element-5131" />
                <p className="mt-1 text-xs text-gray-500" data-id="element-5132">
                  300-500 words recommended
                </p>
              </div>
              <div data-id="element-5133">
                <label htmlFor="motivation" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-5134">
                  Why are you interested in this specific program and
                  university? *
                </label>
                <textarea id="motivation" rows={4} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500" value={formData.questions.motivation} onChange={e => handleInputChange('questions', 'motivation', e.target.value)} required data-id="element-5135" />
                <p className="mt-1 text-xs text-gray-500" data-id="element-5136">
                  300-500 words recommended
                </p>
              </div>
              <div data-id="element-5137">
                <label htmlFor="contribution" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-5138">
                  How do you plan to contribute to the UAE's development after
                  completing your studies? *
                </label>
                <textarea id="contribution" rows={4} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500" value={formData.questions.contribution} onChange={e => handleInputChange('questions', 'contribution', e.target.value)} required data-id="element-5139" />
                <p className="mt-1 text-xs text-gray-500" data-id="element-5140">
                  300-500 words recommended
                </p>
              </div>
              <div data-id="element-5141">
                <label htmlFor="challenges" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-5142">
                  Describe a significant challenge you've faced and how you
                  overcame it. *
                </label>
                <textarea id="challenges" rows={4} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500" value={formData.questions.challenges} onChange={e => handleInputChange('questions', 'challenges', e.target.value)} required data-id="element-5143" />
                <p className="mt-1 text-xs text-gray-500" data-id="element-5144">
                  300-500 words recommended
                </p>
              </div>
              <div data-id="element-5145">
                <label htmlFor="additionalInfo" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-5146">
                  Is there anything else you would like the scholarship
                  committee to know about you?
                </label>
                <textarea id="additionalInfo" rows={4} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500" value={formData.questions.additionalInfo} onChange={e => handleInputChange('questions', 'additionalInfo', e.target.value)} data-id="element-5147" />
                <p className="mt-1 text-xs text-gray-500" data-id="element-5148">Optional</p>
              </div>
            </div>
          </div>;
      case 6:
        return <div className="space-y-6" data-id="element-5149">
            <h2 className="text-xl font-semibold text-gray-900 mb-4" data-id="element-5150">
              Review & Submit
            </h2>
            <p className="text-gray-600 mb-6" data-id="element-5151">
              Please review your application carefully before submitting. You
              cannot make changes after submission.
            </p>
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-6 mb-6" data-id="element-5152">
              <h3 className="font-medium text-gray-900 mb-4 flex items-center" data-id="element-5153">
                <FileTextIcon className="w-5 h-5 mr-2" data-id="element-5154" />
                Application Summary
              </h3>
              <div className="space-y-4" data-id="element-5155">
                <div data-id="element-5156">
                  <h4 className="text-sm font-medium text-gray-500 uppercase" data-id="element-5157">
                    Scholarship
                  </h4>
                  <p className="text-gray-900 font-medium" data-id="element-5158">
                    {scholarship.title}
                  </p>
                  <p className="text-gray-700" data-id="element-5159">
                    {scholarship.university} - {scholarship.program}
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-5160">
                  <div data-id="element-5161">
                    <h4 className="text-sm font-medium text-gray-500 uppercase" data-id="element-5162">
                      Personal Information
                    </h4>
                    <ul className="mt-2 space-y-1" data-id="element-5163">
                      <li className="text-gray-700" data-id="element-5164">
                        <span className="text-gray-500" data-id="element-5165">Name:</span>{' '}
                        {formData.personalInfo.fullName || '[Not provided]'}
                      </li>
                      <li className="text-gray-700" data-id="element-5166">
                        <span className="text-gray-500" data-id="element-5167">Email:</span>{' '}
                        {formData.personalInfo.email || '[Not provided]'}
                      </li>
                      <li className="text-gray-700" data-id="element-5168">
                        <span className="text-gray-500" data-id="element-5169">Phone:</span>{' '}
                        {formData.personalInfo.phone || '[Not provided]'}
                      </li>
                      <li className="text-gray-700" data-id="element-5170">
                        <span className="text-gray-500" data-id="element-5171">Nationality:</span>{' '}
                        {formData.personalInfo.nationality || '[Not provided]'}
                      </li>
                    </ul>
                  </div>
                  <div data-id="element-5172">
                    <h4 className="text-sm font-medium text-gray-500 uppercase" data-id="element-5173">
                      Academic Information
                    </h4>
                    <ul className="mt-2 space-y-1" data-id="element-5174">
                      <li className="text-gray-700" data-id="element-5175">
                        <span className="text-gray-500" data-id="element-5176">Degree:</span>{' '}
                        {formData.academicInfo.highestDegree || '[Not provided]'}
                      </li>
                      <li className="text-gray-700" data-id="element-5177">
                        <span className="text-gray-500" data-id="element-5178">Institution:</span>{' '}
                        {formData.academicInfo.institution || '[Not provided]'}
                      </li>
                      <li className="text-gray-700" data-id="element-5179">
                        <span className="text-gray-500" data-id="element-5180">Major:</span>{' '}
                        {formData.academicInfo.major || '[Not provided]'}
                      </li>
                      <li className="text-gray-700" data-id="element-5181">
                        <span className="text-gray-500" data-id="element-5182">GPA:</span>{' '}
                        {formData.academicInfo.gpa || '[Not provided]'}
                      </li>
                    </ul>
                  </div>
                </div>
                <div data-id="element-5183">
                  <h4 className="text-sm font-medium text-gray-500 uppercase" data-id="element-5184">
                    Documents
                  </h4>
                  <ul className="mt-2 space-y-1" data-id="element-5185">
                    <li className="text-gray-700 flex items-center" data-id="element-5186">
                      <div className={`w-4 h-4 rounded-full mr-2 ${formData.documents.resume ? 'bg-green-500' : 'bg-gray-300'}`} data-id="element-5187"></div>
                      Resume/CV
                    </li>
                    <li className="text-gray-700 flex items-center" data-id="element-5188">
                      <div className={`w-4 h-4 rounded-full mr-2 ${formData.documents.transcripts ? 'bg-green-500' : 'bg-gray-300'}`} data-id="element-5189"></div>
                      Academic Transcripts
                    </li>
                    <li className="text-gray-700 flex items-center" data-id="element-5190">
                      <div className={`w-4 h-4 rounded-full mr-2 ${formData.documents.recommendationLetters ? 'bg-green-500' : 'bg-gray-300'}`} data-id="element-5191"></div>
                      Recommendation Letters
                    </li>
                    <li className="text-gray-700 flex items-center" data-id="element-5192">
                      <div className={`w-4 h-4 rounded-full mr-2 ${formData.documents.statementOfPurpose ? 'bg-green-500' : 'bg-gray-300'}`} data-id="element-5193"></div>
                      Statement of Purpose
                    </li>
                    <li className="text-gray-700 flex items-center" data-id="element-5194">
                      <div className={`w-4 h-4 rounded-full mr-2 ${formData.documents.englishCertificate ? 'bg-green-500' : 'bg-gray-300'}`} data-id="element-5195"></div>
                      English Proficiency Certificate
                    </li>
                    <li className="text-gray-700 flex items-center" data-id="element-5196">
                      <div className={`w-4 h-4 rounded-full mr-2 ${formData.documents.passport ? 'bg-green-500' : 'bg-gray-300'}`} data-id="element-5197"></div>
                      Passport Copy
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="bg-yellow-50 rounded-lg border border-yellow-200 p-4 mb-6" data-id="element-5198">
              <h3 className="font-medium text-yellow-800 mb-2" data-id="element-5199">
                Before You Submit
              </h3>
              <ul className="space-y-2 text-yellow-700 text-sm" data-id="element-5200">
                <li className="flex items-start" data-id="element-5201">
                  <CheckIcon className="w-5 h-5 text-yellow-500 mr-2 flex-shrink-0" data-id="element-5202" />
                  Ensure all information provided is accurate and complete
                </li>
                <li className="flex items-start" data-id="element-5203">
                  <CheckIcon className="w-5 h-5 text-yellow-500 mr-2 flex-shrink-0" data-id="element-5204" />
                  Verify that all required documents have been uploaded
                </li>
                <li className="flex items-start" data-id="element-5205">
                  <CheckIcon className="w-5 h-5 text-yellow-500 mr-2 flex-shrink-0" data-id="element-5206" />
                  Check that your answers to the questionnaire are thorough and
                  well-written
                </li>
              </ul>
            </div>
            <div className="flex items-center mb-6" data-id="element-5207">
              <input type="checkbox" id="declaration" className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" data-id="element-5208" />
              <label htmlFor="declaration" className="ml-2 block text-sm text-gray-700" data-id="element-5209">
                I declare that all information provided is true and accurate. I
                understand that any false information may result in the
                rejection of my application or revocation of the scholarship.
              </label>
            </div>
          </div>;
      default:
        return null;
    }
  };
  return <Layout title={`Apply for ${scholarship.title}`} persona="Student" data-id="element-5210">
      <div className="max-w-5xl mx-auto" data-id="element-5211">
        {/* Back button */}
        <button onClick={() => navigate(`/student/scholarships/${scholarshipId}`)} className="mb-6 inline-flex items-center text-gray-700 hover:text-primary-600 transition-colors" data-id="element-5212">
          <ArrowLeftIcon className="w-4 h-4 mr-1" data-id="element-5213" />
          Back to Scholarship Details
        </button>
        {/* Step progress indicator */}
        <div className="mb-8" data-id="element-5214">
          <nav aria-label="Progress" className="mb-6" data-id="element-5215">
            <ol className="flex items-center justify-between w-full" data-id="element-5216">
              {steps.map((step, index) => <li key={step.id} className="relative flex flex-col items-center" data-id="element-5217">
                  {/* Connecting line before */}
                  {index > 0 && <div className="absolute top-4 right-full w-full h-0.5" aria-hidden="true" data-id="element-5218">
                      <div className={`h-0.5 ${currentStep > index ? 'bg-primary-500' : 'bg-gray-200'}`} style={{
                  width: 'calc(100% - 16px)',
                  marginLeft: '8px'
                }} data-id="element-5219" />
                    </div>}
                  {/* Step indicator */}
                  <button onClick={() => step.id < currentStep && setCurrentStep(step.id)} className={`relative flex h-8 w-8 items-center justify-center rounded-full z-10 
                      ${currentStep > step.id ? 'bg-primary-500 hover:bg-primary-600' : currentStep === step.id ? 'bg-primary-50 border-2 border-primary-500' : 'bg-white border-2 border-gray-300'} 
                      ${step.id < currentStep ? 'cursor-pointer' : 'cursor-default'}`} disabled={step.id >= currentStep} aria-current={currentStep === step.id ? 'step' : undefined} data-id="element-5220">
                    {currentStep > step.id ? <CheckCircleIcon className="h-5 w-5 text-white" aria-hidden="true" data-id="element-5221" /> : <span className={currentStep === step.id ? 'text-primary-500' : 'text-gray-500'} data-id="element-5222">
                        {step.icon}
                      </span>}
                    <span className="sr-only" data-id="element-5223">{step.name}</span>
                  </button>
                  {/* Step label */}
                  <div className="text-center mt-2" data-id="element-5224">
                    <span className={`text-xs font-medium ${currentStep === step.id ? 'text-primary-500' : currentStep > step.id ? 'text-gray-700' : 'text-gray-500'}`} data-id="element-5225">
                      {step.name}
                    </span>
                  </div>
                </li>)}
            </ol>
          </nav>
        </div>
        {/* Form content */}
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm mb-6" data-id="element-5226">
          {renderStep()}
        </div>
        {/* Navigation buttons */}
        <div className="flex justify-between mt-6" data-id="element-5227">
          <button type="button" onClick={handlePrevious} disabled={currentStep === 1} className={`px-4 py-2 rounded-md flex items-center text-sm font-medium ${currentStep === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-id="element-5228">
            <ArrowLeftIcon className="w-4 h-4 mr-2" data-id="element-5229" />
            Previous
          </button>
          <div className="flex space-x-3" data-id="element-5230">
            {/* Save Draft button available on all steps */}
            <button type="button" onClick={handleSaveDraft} className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-md flex items-center text-sm font-medium hover:bg-gray-50" data-id="element-5231">
              <SaveIcon className="w-4 h-4 mr-2" data-id="element-5232" />
              Save Draft
            </button>
            {currentStep === steps.length ? <button type="button" onClick={handleSubmit} className="bg-primary-500 text-white px-4 py-2 rounded-md flex items-center text-sm font-medium hover:bg-primary-600" data-id="element-5233">
                <CheckCircleIcon className="w-4 h-4 mr-2" data-id="element-5234" />
                Submit Application
              </button> : <button type="button" onClick={handleNext} className="bg-primary-500 text-white px-4 py-2 rounded-md flex items-center text-sm font-medium hover:bg-primary-600" data-id="element-5235">
                Next
                <ArrowRightIcon className="w-4 h-4 ml-2" data-id="element-5236" />
              </button>}
          </div>
        </div>
      </div>
    </Layout>;
};
export default ScholarshipApplication;